import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { useLazyGetFilesQuery, useUpdateFileMutation } from "@dashboard/files/api";
import { FileInfo } from "@dashboard/files/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Typography } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { UncontrolledTextField } from "@shared/form/TextField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const getExtension = (value: string) => {
    const lastDotIndex = value.lastIndexOf(".");
    if (lastDotIndex <= 0) {
        return "";
    }
    return value.slice(lastDotIndex);
};

const useStyles = makeStyles()((theme) => ({
    textContainer: {
        display: "flex",
        alignItems: "center",
        marginTop: theme.spacing(1),
    },
    category: {
        marginTop: theme.spacing(2),
    },
    copyButton: {
        marginLeft: theme.spacing(2),
    },
}));
interface Props {
    file: FileInfo | null;
    isOpen: boolean;
    closeDialog: () => void;
}

export const FileDetailsModal = ({ file, isOpen, closeDialog }: Props) => {
    const { t } = useTranslation(["common", "modals"]);
    const { classes } = useStyles();

    const { data: workspace } = useCurrentWorkspace();
    const currentWorkspaceGroups = useSelector(selectGroups);
    const { groupAbility } = PermissionService();

    const [updateFile] = useUpdateFileMutation();

    const [filename, setFilename] = useState(file?.filename || "");
    const [error, setError] = useState<string | null>(null);

    const [getFiles] = useLazyGetFilesQuery();

    useEffect(() => {
        if (isOpen && file) {
            setFilename(file.filename || "");
            setError(null);
        }
    }, [isOpen, file]);

    const currentGroup = currentWorkspaceGroups.find((item) => item.id === file?.groupId);
    const mayManageFile = groupAbility(currentGroup || null, workspace).can("manage", "File");

    const copyToClipboard = useCallback(
        (text: string | undefined) => {
            if (!text) {
                return;
            }
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    showSuccessToast(t("common:toast.copiedToClipboardSuccess"));
                })
                .catch((_error) => {
                    showErrorToast(t("common:toast.copiedToClipboardError"));
                });
        },
        [t],
    );

    const validateForm = async (values: { filename: string }) => {
        const errors: { [key: string]: string } = {};

        if (!file) {
            return errors;
        }

        if (values.filename === file.filename) {
            return errors;
        }

        const originalExtension = getExtension(file.filename);
        const newExtension = getExtension(values.filename);

        if (!values.filename.trim()) {
            errors.filename = t("modals:fileDetails.input.fileName.validation.empty");
            return errors;
        }

        if (originalExtension && originalExtension !== newExtension) {
            errors.filename = i18next.t("modals:fileDetails.input.fileName.validation.extensionChanged");
            return errors;
        }

        if (!/^[a-zA-Z0-9.-]+$/.test(values.filename)) {
            errors.filename = t("modals:fileDetails.input.fileName.validation.invalid");
            return errors;
        }

        await getFiles({
            workspaceId: workspace?.id,
            filename: values.filename,
        })
            .unwrap()
            .then((res) => {
                const isDuplicate = res.list?.some(
                    (existingFile) =>
                        existingFile.filename === values.filename &&
                        existingFile.id !== file.id &&
                        existingFile.groupId === file.groupId,
                );

                if (isDuplicate) {
                    errors.filename = t("modals:fileDetails.input.fileName.validation.exist");
                }
            });

        return errors;
    };

    const handleFilenameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilename(e.target.value);
        setError(null);
    };

    const handleSubmit = async () => {
        if (!file) {
            return;
        }

        const validationErrors = await validateForm({ filename });
        if (validationErrors.filename) {
            setError(validationErrors.filename);
            return;
        }

        try {
            await updateFile({ id: file.id, filename });
            showSuccessToast(t("modals:fileDetails.toast.fileNameUpdatedSuccess"));
            closeDialog();
        } catch (err) {
            const errorMessage = err as Error;
            setError(errorMessage.message || t("modals:fileDetails.toast.fileNameUpdatedError"));
        }
    };

    const isPristine = file?.filename === filename;

    const getTooltipText = (hasManagePermission: boolean, isFormPristine: boolean): string => {
        if (!hasManagePermission) {
            return t("modals:fileDetails.tooltip.notAllowedToEdit");
        }

        if (isFormPristine) {
            return t("modals:fileDetails.tooltip.noChangesToSave");
        }

        return "";
    };

    return (
        <Modal
            title={t("modals:fileDetails.title")}
            open={isOpen}
            onClose={closeDialog}
            endButton={
                <PrimaryButton
                    onClick={handleSubmit}
                    disabled={!mayManageFile || isPristine}
                    tooltipProps={{
                        title: getTooltipText(mayManageFile, isPristine),
                    }}
                >
                    {t("common:button.save")}
                </PrimaryButton>
            }
        >
            <Typography variant="h6" className={classes.category}>
                {t("common:label.general")}
            </Typography>

            <UncontrolledTextField
                name="filename"
                label={t("modals:fileDetails.input.fileName.caption")}
                value={filename}
                onChange={handleFilenameChange}
                disabled={!mayManageFile}
                errorMessage={error || undefined}
                fullWidth
            />

            <UncontrolledTextField
                label={t("common:label.uploader")}
                value={file?.uploader}
                disabled
                fullWidth
            />

            {file?.createdDate && (
                <UncontrolledTextField
                    label={t("modals:fileDetails.input.updated.caption")}
                    value={TimeService.getLocalDate(file.createdDate, "ISO", "dateTime")}
                    disabled
                    fullWidth
                />
            )}

            {file?.metadata?.file?.generic?.hash?.sha256 && (
                <>
                    <Typography variant="h6" className={classes.category}>
                        {t("modals:fileDetails.validation")}
                    </Typography>
                    <div className={classes.textContainer}>
                        <UncontrolledTextField
                            label={t("modals:fileDetails.input.sha256hex.caption")}
                            value={file.metadata.file.generic.hash.sha256.hex}
                            disabled
                            fullWidth
                        />
                        <SecondaryButton
                            className={classes.copyButton}
                            onClick={() => copyToClipboard(file?.metadata?.file?.generic?.hash?.sha256?.hex)}
                            tooltipProps={{ title: t("common:button.copy") }}
                        >
                            <i className="fa-solid fa-copy" />
                        </SecondaryButton>
                    </div>
                    <div className={classes.textContainer}>
                        <UncontrolledTextField
                            label={t("modals:fileDetails.input.sha256base64.caption")}
                            value={file.metadata.file.generic.hash.sha256.base64}
                            disabled
                            fullWidth
                        />
                        <SecondaryButton
                            className={classes.copyButton}
                            onClick={() => copyToClipboard(file?.metadata?.file?.generic?.hash?.sha256?.base64)}
                            tooltipProps={{ title: t("common:button.copy") }}
                        >
                            <i className="fa-solid fa-copy" />
                        </SecondaryButton>
                    </div>
                </>
            )}

            {file?.metadata?.file?.apk && (
                <Typography variant="h6" className={classes.category}>
                    {t("modals:fileDetails.android")}
                </Typography>
            )}

            {file?.metadata?.file?.apk?.manifest?.versioncode && (
                <div className={classes.textContainer}>
                    <UncontrolledTextField
                        label={t("modals:fileDetails.input.version.caption")}
                        value={file.metadata.file.apk.manifest.versioncode}
                        disabled
                        fullWidth
                    />
                    <SecondaryButton
                        className={classes.copyButton}
                        onClick={() => copyToClipboard(file?.metadata?.file?.apk?.manifest?.versioncode)}
                        tooltipProps={{ title: t("common:button.copy") }}
                    >
                        <i className="fa-solid fa-copy" />
                    </SecondaryButton>
                </div>
            )}

            {file?.metadata?.file?.apk?.manifest?.versionname && (
                <div className={classes.textContainer}>
                    <UncontrolledTextField
                        label={t("modals:fileDetails.input.versionName.caption")}
                        value={file.metadata.file.apk.manifest.versionname}
                        disabled
                        fullWidth
                    />
                    <SecondaryButton
                        className={classes.copyButton}
                        onClick={() => copyToClipboard(file?.metadata?.file?.apk?.manifest?.versionname)}
                        tooltipProps={{ title: t("common:button.copy") }}
                    >
                        <i className="fa-solid fa-copy" />
                    </SecondaryButton>
                </div>
            )}

            {file?.metadata?.file?.apk?.signers?.map((signer, index) => (
                <React.Fragment key={index}>
                    <Typography variant="subtitle1" className={classes.category}>
                        {t("modals:fileDetails.signer", { index: index + 1 })}
                    </Typography>
                    <div className={classes.textContainer}>
                        <UncontrolledTextField
                            label={t("modals:fileDetails.input.digest.caption")}
                            value={signer.digests.find((d) => d.type === "sha-256")?.certificate}
                            disabled
                            fullWidth
                        />
                        <SecondaryButton
                            className={classes.copyButton}
                            tooltipProps={{ title: t("common:button.copy") }}
                            onClick={() => copyToClipboard(signer.digests.find((d) => d.type === "sha-256")?.certificate)}
                        >
                            <i className="fa-solid fa-copy" />
                        </SecondaryButton>
                    </div>
                    <div className={classes.textContainer}>
                        <UncontrolledTextField
                            label={t("modals:fileDetails.input.dn.caption")}
                            value={Object.entries(signer.distinguishedName)
                                .filter(([_key, value]) => value !== "unknown")
                                .map(([key, value]) => `${key}=${value}`)
                                .join(", ")}
                            disabled
                            fullWidth
                        />
                        <SecondaryButton
                            className={classes.copyButton}
                            onClick={() =>
                                copyToClipboard(
                                    Object.entries(signer.distinguishedName)
                                        .filter(([_key, value]) => value !== "unknown")
                                        .map(([key, value]) => `${key}=${value}`)
                                        .join(", "),
                                )
                            }
                            tooltipProps={{ title: t("common:button.copy") }}
                        >
                            <i className="fa-solid fa-copy" />
                        </SecondaryButton>
                    </div>
                </React.Fragment>
            ))}
        </Modal>
    );
};
